const DIRECT_MEASUREMENT_CODES = new Set([
     "ambient.co2",
     "canopy.air_temperature",
     "canopy.leaf_temperature",
     "canopy.relative_humidity",
     "light.illuminance",
     "plant.leaf_signal_voltage",
     "substrate.bulk_ec",
     "substrate.dielectric_permittivity",
     "substrate.temperature",
     "substrate.vwc",
])

const CALCULATED_CODES = new Set([
     "canopy.leaf_vpd",
])

const ESTIMATED_CODES = new Set([
     "light.dli",
     "light.edli",
     "light.eppfd",
     "light.ppfd",
     "substrate.pore_water_ec",
])

const KNOWN_QUALITY_FLAGS = new Set([
     "calibrated",
     "estimated",
     "invalid",
     "mixed",
     "stale",
     "uncalibrated",
     "unverified",
     "valid",
])

const EVIDENCE_ORDER = [
     "measured",
     "calculated",
     "estimated",
     "proxy",
     "calibrated",
     "calibration_not_documented",
     "uncalibrated",
     "mixed",
     "invalid",
     "stale",
     "unverified",
     "unavailable",
     "unknown",
]

const MEASUREMENT_EVIDENCE_KINDS = new Set(["measured", "calculated", "estimated", "proxy"])
const MEASUREMENT_EVIDENCE_PAIRS = new Set([
     "sensor_output:measured",
     "vendor_estimate_not_recomputed:estimated",
])
const DOCUMENTED_CALIBRATION_STATES = new Set(["calibrated", "documented"])
const EXPLICIT_EVIDENCE_KINDS = new Set(["measured", "calculated", "estimated", "proxy"])
const EXPLICIT_EVIDENCE_PAIRS = new Set([
     "sensor_output:measured",
     "calculated:calculated",
     "proxy:proxy",
])

const normalizedEvidenceValue = (value) => typeof value === "string" ? value.trim().toLowerCase() : ""

const measurementEvidenceDetails = (measurements) => Array.from(new Set(measurements.map((measurement) => [
     ["method", normalizedEvidenceValue(measurement?.method)],
     ["evidenceKind", normalizedEvidenceValue(measurement?.evidenceKind)],
     ["calibrationStatus", normalizedEvidenceValue(measurement?.calibrationStatus)],
].filter(([, value]) => value).map(([field, value]) => `${field}=${value}`).join(", ")).filter(Boolean))).sort()

export const classifyMeasurementEvidence = (measurements = []) => {
     const samples = (measurements || []).filter(Boolean)
     const details = measurementEvidenceDetails(samples)
     const base = {
          flags: [],
          details,
          descriptionKey: "sensor.evidenceAria",
          calibrationDocumented: false,
     }

     if (!samples.length) {
          return {
               ...base,
               kind: "unknown",
               state: "unknown",
               badges: ["unknown", "calibration_not_documented"],
          }
     }

     const methods = new Set(samples.map((measurement) => normalizedEvidenceValue(measurement.method)))
     const kinds = new Set(samples.map((measurement) => normalizedEvidenceValue(measurement.evidenceKind)))
     const calibrationStates = new Set(samples.map((measurement) => normalizedEvidenceValue(measurement.calibrationStatus)))
     const conflict = methods.size > 1 || kinds.size > 1 || calibrationStates.size > 1

     if (conflict) {
          return {
               ...base,
               kind: "unknown",
               state: "mixed",
               badges: ["mixed", "unknown"],
          }
     }

     const [method = ""] = methods
     const [kind = ""] = kinds
     const [calibrationStatus = ""] = calibrationStates
     const pairIsDocumented = MEASUREMENT_EVIDENCE_KINDS.has(kind)
          && MEASUREMENT_EVIDENCE_PAIRS.has(`${method}:${kind}`)
     const calibrationDocumented = DOCUMENTED_CALIBRATION_STATES.has(calibrationStatus)
     const calibrationBadge = calibrationDocumented ? "calibrated" : "calibration_not_documented"

     if (!pairIsDocumented) {
          return {
               ...base,
               kind: "unknown",
               state: "unknown",
               badges: ["unknown", calibrationBadge],
               calibrationDocumented,
          }
     }

     return {
          ...base,
          kind,
          state: calibrationDocumented ? "valid" : "not_documented",
          badges: [kind, calibrationBadge],
          calibrationDocumented,
     }
}

export const resolveEvidenceContractField = (payload, responsePath) => {
     const contract = payload?.evidence_contract
     if (contract?.version !== "feeder.dashboard.evidence.v1"
          || contract.read_only !== true
          || contract.controls_hardware !== false) return null
     const metadata = contract.fields?.[responsePath]
     return metadata && typeof metadata === "object" && !Array.isArray(metadata) ? metadata : null
}

export const classifyExplicitEvidence = (metadata) => {
     const kind = normalizedEvidenceValue(metadata?.evidence_kind ?? metadata?.evidenceKind)
     const method = normalizedEvidenceValue(metadata?.method)
     const calibrationStatus = normalizedEvidenceValue(metadata?.calibration_status ?? metadata?.calibrationStatus)
     const flags = Array.from(new Set([
          ...(Array.isArray(metadata?.quality_flags) ? metadata.quality_flags : []),
          ...(Array.isArray(metadata?.qualityFlags) ? metadata.qualityFlags : []),
     ].map(normalizedEvidenceValue).filter(Boolean))).sort()
     const calibrationDocumented = DOCUMENTED_CALIBRATION_STATES.has(calibrationStatus)
     const calibrationBadge = calibrationDocumented
          ? "calibrated"
          : calibrationStatus === "uncalibrated"
          ? "uncalibrated"
          : "calibration_not_documented"
     const details = [
          method && `method=${method}`,
          kind && `evidenceKind=${kind}`,
          calibrationStatus && `calibrationStatus=${calibrationStatus}`,
          metadata?.basis && `basis=${String(metadata.basis)}`,
          metadata?.unit && `unit=${String(metadata.unit)}`,
     ].filter(Boolean)
     const base = {
          flags,
          details,
          descriptionKey: "sensor.evidenceAria",
          calibrationDocumented,
     }
     const pairIsDocumented = EXPLICIT_EVIDENCE_KINDS.has(kind)
          && EXPLICIT_EVIDENCE_PAIRS.has(`${method}:${kind}`)
     if (!pairIsDocumented) {
          return { ...base, kind: "unknown", state: "unknown", badges: ["unknown", calibrationBadge] }
     }
     const explicitState = ["invalid", "stale", "unverified"].find((flag) => flags.includes(flag))
     const badges = [kind, calibrationBadge]
     if (explicitState) badges.push(explicitState)
     return {
          ...base,
          kind,
          state: explicitState || (calibrationBadge === "uncalibrated" ? "uncalibrated" : "valid"),
          badges: Array.from(new Set(badges)),
     }
}

export const dashboardFieldEvidence = (payload, responsePath) => classifyExplicitEvidence(
     resolveEvidenceContractField(payload, responsePath),
)

const baseEvidenceFor = (code) => {
     if (code.includes("water_status") || code.includes("hydration") || code.includes("turgor")) return "proxy"
     if (code.startsWith("light.spectrum.estimated_ppfd.") || code.startsWith("light.spectrum.relative.")) return "estimated"
     if (code.startsWith("light.spectrum.raw.")) return "measured"
     if (DIRECT_MEASUREMENT_CODES.has(code)) return "measured"
     if (CALCULATED_CODES.has(code)) return "calculated"
     if (ESTIMATED_CODES.has(code)) return "estimated"
     return "unknown"
}

const normalizedFlags = (points) => Array.from(new Set((points || []).flatMap((point) => {
     const flags = Array.isArray(point?.qualityFlags) ? point.qualityFlags : []
     return [...flags, point?.qualityFlag]
          .filter((flag) => typeof flag === "string" && flag.trim())
          .map((flag) => flag.trim().toLowerCase())
}))).sort()

const nonDirectProvenance = (kind) => ["calculated", "estimated", "proxy"].includes(kind) ? [kind] : []

const visibleStateBadges = (flags) => ["invalid", "stale", "unverified", "uncalibrated"]
     .filter((flag) => flags.includes(flag))

export const classifyMetricEvidence = ({ code = "", points = [], calibrationReference = "" } = {}) => {
     if (!points.length) {
          return {
               kind: "unknown",
               state: "unavailable",
               badges: ["unavailable"],
               flags: [],
               calibrationDocumented: false,
          }
     }

     const kind = baseEvidenceFor(code)
     const flags = normalizedFlags(points)
     const pointFlags = new Set((points || []).map((point) => String(point?.qualityFlag || "").trim().toLowerCase()).filter(Boolean))
     const mixed = flags.includes("mixed") || pointFlags.size > 1
     const unknownQuality = !flags.length || flags.some((flag) => !KNOWN_QUALITY_FLAGS.has(flag))
     const calibrationDocumented = String(calibrationReference || "").trim().length > 0

     if (mixed) {
          const badges = [...nonDirectProvenance(kind), "mixed", ...visibleStateBadges(flags)]
          return { kind, state: "mixed", badges, flags, calibrationDocumented }
     }

     if (kind === "unknown" || unknownQuality) {
          const badges = [...nonDirectProvenance(kind), "unknown", ...visibleStateBadges(flags)]
          return { kind, state: "unknown", badges, flags, calibrationDocumented }
     }

     const explicitState = ["invalid", "stale", "unverified"].find((flag) => flags.includes(flag))
     if (explicitState) {
          const badges = [...nonDirectProvenance(kind), explicitState]
          if (flags.includes("uncalibrated")) badges.push("uncalibrated")
          return { kind, state: explicitState, badges, flags, calibrationDocumented }
     }

     const badges = [kind]
     if (flags.includes("estimated") && kind !== "estimated") badges.push("estimated")
     if (flags.includes("uncalibrated")) badges.push("uncalibrated")
     else if (calibrationDocumented || flags.includes("calibrated")) badges.push("calibrated")

     return {
          kind,
          state: flags.includes("uncalibrated") ? "uncalibrated" : flags.includes("estimated") ? "estimated" : "valid",
          badges: Array.from(new Set(badges)),
          flags,
          calibrationDocumented,
     }
}

export const chartPointsForEvidence = (points = []) => points.map((point) => {
     const flags = normalizedFlags([point])
     if (!flags.includes("invalid")) return point
     return { ...point, value: null, minimum: null, maximum: null, excludedFromAnalysis: true }
})

export const evidenceLegendLevels = (evidenceItems = []) => {
     const present = new Set(evidenceItems.flatMap((item) => item?.badges || []))
     return EVIDENCE_ORDER.filter((level) => present.has(level))
}

export const evidenceTranslationKey = (level) => `chuck.log.evidence.${EVIDENCE_ORDER.includes(level) ? level : "unknown"}`
